import type {
  Answer,
  AnswerValue,
  FieldDefinition,
  FieldId,
  ValidationIssue,
} from '@inside-us-aos/domain';

import {
  createAnswerStore,
  type AnswerChangeListener,
  type AnswerStore,
  type AnswerStoreMetadata,
} from '../state/answerStore';
import {
  createAuditEmitter,
  type AuditEmitter,
  type AuditEvent,
  type AuditEventListener,
  type AuditPayload,
  type RedactedValue,
} from '../audit/emitter';
import { computeDefaultPiiRedactionSet } from '../audit/redaction';
import {
  resolveFieldStatus,
  type FieldStatus,
} from './visibility';
import {
  validateAnswers,
  type ValidateAnswersResult,
} from '../validation/oneOfGroups';
import {
  enforceRepeatableBounds,
  resolveAllRepeatableGroupStates,
  resolveRepeatableGroupState,
  type RepeatableBoundIssue,
  type RepeatableGroupState,
} from '../repeatables/state';
import {
  resolveSectionProgress,
  resolveSectionStatus,
  type SectionProgress,
} from '../progress/sectionProgress';
import type {
  AnswerPersistenceAdapter,
  PersistedLoadResult,
  PersistedSnapshot,
} from '../persistence/adapter';

/**
 * `createFormEngine` façade.
 *
 * Bundles the slice-2-through-slice-9 primitives into a single
 * consumer-facing object. Phase 2's React layer wires its
 * `useSyncExternalStore` adapter against this façade; Phase 3's
 * screener composes a subset of the same primitives over the
 * `eligibility.*` namespace.
 *
 * The façade owns:
 *   - an `AnswerStore` (Slice 7) — the in-memory state container.
 *   - an `AuditEmitter` (Slice 8) — the audit-event stream.
 *   - read-throughs to visibility / required (Slice 2), validation
 *     (Slice 3 + 4), repeatable-group state (Slice 5), and section
 *     progress (Slice 6).
 *   - hydrate / persist hooks that round-trip through any
 *     `AnswerPersistenceAdapter` (Slice 9).
 *
 * The engine itself is stateless beyond the answer store and the
 * listener sets. Every derived view (`getValidationState`,
 * `getSectionProgress`, etc.) is computed on demand from the current
 * answers — no incremental caching. Phase 2 may layer memoization on
 * top using its own React hooks.
 */

export interface FormEngineMetadata
  extends Omit<AnswerStoreMetadata, 'now'> {
  /** Override for the wall-clock — useful for deterministic tests. */
  now?: () => number;
  /**
   * Override the default PII redaction set passed to the audit
   * emitter. When omitted, the engine computes a conservative set
   * from the catalog via field-id pattern matching (see
   * `audit/redaction.ts`). Pass an empty set to disable redaction
   * (e.g. for in-engine debug tooling that needs raw values);
   * pass a custom set to extend or replace the default policy.
   */
  redactedFields?: ReadonlySet<FieldId>;
  /**
   * Forward `coalesceMs` to the audit
   * emitter. ADR 0019 recommends 800–1200 ms for live audit-panel
   * consumers so per-keystroke noise collapses into one event per
   * "stable state" of a field. Omit (or pass `0`/`undefined`) for
   * per-event emission. The audit emitter's coalescing semantics
   * (oldValue from first event, newValue + timestamp from last,
   * dispose-flush) are documented at
   * `packages/form-engine/src/audit/emitter.ts`.
   */
  coalesceMs?: number;
}

/**
 * Reason for a hydrate operation rolling back. The engine surfaces
 * one of these alongside the adapter's own warnings when replay
 * fails — callers can decide whether to retry against an older
 * catalog, prompt the user for a manual migration, or surface the
 * error.
 */
export interface HydratePartialReplayWarning {
  code: 'partial_replay_failure';
  /** The field id that triggered the replay failure (if known). */
  failedFieldId?: FieldId;
  /** Stringified error message captured during replay. */
  error: string;
}

/**
 * Per-row warning emitted when a saved answer's `fieldId` is no
 * longer present in the current catalog (the field was renamed or
 * removed since the snapshot was written). The hydrate skips that
 * single row and applies every other answer; no rollback. Callers
 * can log these for audit / migration tooling.
 */
export interface HydrateUnknownFieldSkippedWarning {
  code: 'unknown_field_skipped';
  /** The stale field id that the catalog no longer recognizes. */
  fieldId: string;
  /** groupInstanceId if the dropped field belonged to a repeatable group. */
  groupInstanceId?: string;
}

export type HydrateWarning =
  | PersistedLoadResult['warnings'][number]
  | HydratePartialReplayWarning
  | HydrateUnknownFieldSkippedWarning;

export interface FormEngineHydrateResult {
  /**
   * True when the snapshot was loaded AND every answer replayed
   * cleanly into the store. Falls back to `false` on either no
   * snapshot found OR a partial-replay failure (in which case
   * `warnings` carries a `partial_replay_failure` entry and the
   * store is rolled back to its pre-hydrate state).
   */
  hydrated: boolean;
  /**
   * Adapter-forwarded warnings (e.g., `schema_version_mismatch`)
   * plus any engine-level `partial_replay_failure` warning.
   */
  warnings: readonly HydrateWarning[];
}

export interface FormEngine {
  // ----- answer-store passthroughs -----
  setAnswer: (
    fieldId: FieldId,
    value: AnswerValue,
    groupInstanceId?: string,
  ) => void;
  getAnswer: (
    fieldId: FieldId,
    groupInstanceId?: string,
  ) => AnswerValue | undefined;
  clearAnswer: (fieldId: FieldId, groupInstanceId?: string) => void;
  getAllAnswers: () => readonly Answer[];

  // ----- derived state -----
  getFieldStatus: (
    fieldId: FieldId,
    groupInstanceId?: string,
  ) => FieldStatus;
  /**
   * Composite validation pass: per-field issues + one-of-group issues
   * + repeatable-bound issues. The result is the engine's single
   * source of truth for "is this answer set clean?". For consumers
   * that need only the bound-issue subset (e.g., a repeatable-section
   * UI that wants to show min/max errors inline near the add-button),
   * use `getRepeatableBoundIssues()`.
   */
  getValidationState: () => ValidateAnswersResult;
  getRepeatableState: (groupId: string) => RepeatableGroupState | null;
  getAllRepeatableStates: () => ReadonlyMap<string, RepeatableGroupState>;
  /**
   * Returns the repeatable-bound issues only. Mirrors the bound
   * issues that `getValidationState()` already includes — same
   * objects, same ordering. Use this when you need a focused list
   * without re-filtering the composite issue array.
   */
  getRepeatableBoundIssues: () => readonly RepeatableBoundIssue[];
  getSectionProgress: (sectionId: string) => SectionProgress | null;
  getAllSectionProgress: () => ReadonlyMap<string, SectionProgress>;

  // ----- subscriptions -----
  /** Subscribes to answer changes (Slice 7 events). */
  subscribe: (listener: AnswerChangeListener) => () => void;
  /** Subscribes to audit events (Slice 8 events). */
  subscribeAudit: (listener: AuditEventListener) => () => void;
  /**
   * Force-flush every pending audit bucket. Use this to mark a
   * stable point in the user's session — section navigation,
   * explicit save, tab hide — so the audit trail records the value
   * AT that boundary instead of waiting for the coalesce timer.
   * Idempotent.
   */
  flushPendingAudit: () => void;

  // ----- persistence -----
  /**
   * Loads a snapshot from `adapter` for `applicationId` and replays
   * its answers into the store. When no snapshot exists, the store is
   * left untouched and `{ hydrated: false }` is returned. The engine
   * passes `expectedSchemaVersion = metadata.schemaVersion` so
   * mismatches surface as warnings.
   */
  hydrate: (
    adapter: AnswerPersistenceAdapter,
    applicationId: string,
  ) => Promise<FormEngineHydrateResult>;
  /**
   * Saves the current answer set as a `PersistedSnapshot` to
   * `adapter`. The snapshot's `schemaVersion` is the engine's
   * `metadata.schemaVersion`; the `savedAt` timestamp is `now()`.
   */
  persist: (
    adapter: AnswerPersistenceAdapter,
    applicationId: string,
  ) => Promise<void>;

  // ----- internals exposed for advanced use / tests -----
  /** Direct handle to the underlying store. Phase 2 uses this for `useSyncExternalStore`. */
  readonly store: AnswerStore;
  /** Direct handle to the underlying audit emitter. */
  readonly audit: AuditEmitter;
  /** The catalog the engine was bound to. */
  readonly catalog: readonly FieldDefinition[];
}

export const createFormEngine = (
  catalog: readonly FieldDefinition[],
  metadata: FormEngineMetadata,
): FormEngine => {
  const now = metadata.now ?? (() => Date.now());
  const store = createAnswerStore(catalog, {
    applicationId: metadata.applicationId,
    schemaVersion: metadata.schemaVersion,
    updatedBy: metadata.updatedBy,
    now,
  });
  const redactedFields =
    metadata.redactedFields ?? computeDefaultPiiRedactionSet(catalog);
  const audit = createAuditEmitter(store, {
    redactedFields,
    coalesceMs: metadata.coalesceMs,
  });

  const fieldsById = new Map<FieldId, FieldDefinition>(
    catalog.map(field => [field.id, field]),
  );

  const getFieldStatus: FormEngine['getFieldStatus'] = (
    fieldId,
    groupInstanceId,
  ) => {
    const field = fieldsById.get(fieldId);
    if (!field) {
      throw new Error(`Unknown field id: ${fieldId}`);
    }
    // Per-instance visibility scoping for repeatable groups.
    //
    // Before this fix, visibility rules were evaluated against the
    // full answer set with a Map(fieldId → value) — so for any rule
    // referencing a sibling field in the SAME repeatable group, the
    // last-written instance's value would clobber every other
    // instance's view. Concrete bug surfaced 2026-05-26: a customer
    // VA marked Employment 1 = unemployed, then filled Employment 2.
    // Row 2's employer_name visibility rule looked up
    // `employment_history.status` by fieldId only, found row 2's
    // 'employed', and the rule fired TRUE for BOTH rows. Worse,
    // toggling row 1's status back to 'employed' surfaced row 2's
    // employer data on row 1's card (display reads were instance-
    // scoped but visibility wasn't, so rows toggled in and out as
    // values bled across the Map).
    //
    // Fix: when evaluating for a repeatable field with an instance
    // id, filter answers so same-group siblings keep only the
    // instance-scoped entry; other groups / singletons pass through.
    if (
      groupInstanceId !== undefined &&
      field.repeatableGroupId !== undefined
    ) {
      const groupId = field.repeatableGroupId;
      const scoped = store.getAllAnswers().filter(a => {
        const sibling = fieldsById.get(a.fieldId);
        if (sibling?.repeatableGroupId === groupId) {
          return a.groupInstanceId === groupInstanceId;
        }
        return a.groupInstanceId === undefined;
      });
      return resolveFieldStatus(field, scoped);
    }
    return resolveFieldStatus(field, store.getAllAnswers());
  };

  const getValidationState: FormEngine['getValidationState'] = () =>
    validateAnswers(catalog, store.getAllAnswers());

  const getRepeatableState: FormEngine['getRepeatableState'] = groupId =>
    resolveRepeatableGroupState(catalog, groupId, store.getAllAnswers());

  const getAllRepeatableStates: FormEngine['getAllRepeatableStates'] = () =>
    resolveAllRepeatableGroupStates(catalog, store.getAllAnswers());

  const getRepeatableBoundIssues: FormEngine['getRepeatableBoundIssues'] = () =>
    enforceRepeatableBounds(catalog, store.getAllAnswers());

  const getSectionProgress: FormEngine['getSectionProgress'] = sectionId =>
    resolveSectionStatus(catalog, sectionId, store.getAllAnswers());

  const getAllSectionProgress: FormEngine['getAllSectionProgress'] = () =>
    resolveSectionProgress(catalog, store.getAllAnswers());

  const hydrate: FormEngine['hydrate'] = async (adapter, applicationId) => {
    const result = await adapter.load(applicationId, {
      expectedSchemaVersion: metadata.schemaVersion,
    });
    if (!result.snapshot) {
      return { hydrated: false, warnings: result.warnings };
    }

    // Two failure shapes during replay:
    //   1. "Unknown field id" — the catalog dropped or renamed this
    //      field since the snapshot was written. Skip just that row
    //      with an `unknown_field_skipped` warning; keep applying
    //      the rest. Rolling back on this would silently empty the
    //      form for every existing user the moment a single field
    //      is removed from the catalog (regression: commit 98bfbab
    //      removed `name.other.count` and every drafted user lost
    //      every other answer on next load).
    //   2. Anything else (type mismatch, store invariant
    //      violation) — those are real engine bugs. Roll back so we
    //      don't half-apply unsafe state, and emit a
    //      `partial_replay_failure` warning.
    const appliedSlots: Array<{
      fieldId: FieldId;
      groupInstanceId: string | undefined;
    }> = [];
    const perRowWarnings: HydrateUnknownFieldSkippedWarning[] = [];

    const UNKNOWN_FIELD_PREFIX = 'Unknown field id:';

    // Suspend the audit emitter for the duration of replay only —
    // the underlying store still emits its change events, so React
    // subscribers (useEngineRevision) and any UI hooks see the
    // hydrated state immediately. Suppressing audit alone keeps the
    // audit-writer from persisting replay events as user edits
    // (production saw 214k audit rows from this leak).
    const resumeAudit = audit.suspend();
    try {
      try {
        for (const answer of result.snapshot.answers) {
          try {
            store.setAnswer(
              answer.fieldId,
              answer.value as AnswerValue,
              answer.groupInstanceId,
            );
            appliedSlots.push({
              fieldId: answer.fieldId,
              groupInstanceId: answer.groupInstanceId,
            });
          } catch (rowErr) {
            const rowError =
              rowErr instanceof Error ? rowErr : new Error(String(rowErr));
            if (rowError.message.startsWith(UNKNOWN_FIELD_PREFIX)) {
              const warning: HydrateUnknownFieldSkippedWarning = {
                code: 'unknown_field_skipped',
                fieldId: answer.fieldId,
                ...(answer.groupInstanceId !== undefined
                  ? { groupInstanceId: answer.groupInstanceId }
                  : {}),
              };
              perRowWarnings.push(warning);
              continue;
            }
            // Non-skippable error — rethrow into the outer catch so
            // we roll back what we've applied so far.
            throw rowError;
          }
        }
      } catch (err) {
        for (let i = appliedSlots.length - 1; i >= 0; i -= 1) {
          const slot = appliedSlots[i]!;
          try {
            store.clearAnswer(slot.fieldId, slot.groupInstanceId);
          } catch {
            // Best-effort cleanup.
          }
        }
        const error = err instanceof Error ? err : new Error(String(err));
        const partial: HydratePartialReplayWarning = {
          code: 'partial_replay_failure',
          error: error.message,
        };
        return {
          hydrated: false,
          warnings: [...result.warnings, ...perRowWarnings, partial],
        };
      }
    } finally {
      resumeAudit();
    }
    return {
      hydrated: true,
      warnings: [...result.warnings, ...perRowWarnings],
    };
  };

  const persist: FormEngine['persist'] = async (adapter, applicationId) => {
    const snapshot: PersistedSnapshot = {
      schemaVersion: metadata.schemaVersion,
      answers: store.getAllAnswers(),
      savedAt: new Date(now()).toISOString(),
    };
    await adapter.save(applicationId, snapshot);
  };

  return {
    setAnswer: store.setAnswer,
    getAnswer: store.getAnswer,
    clearAnswer: store.clearAnswer,
    getAllAnswers: store.getAllAnswers,
    getFieldStatus,
    getValidationState,
    getRepeatableState,
    getAllRepeatableStates,
    getRepeatableBoundIssues,
    getSectionProgress,
    getAllSectionProgress,
    subscribe: store.subscribe,
    subscribeAudit: audit.subscribe,
    flushPendingAudit: audit.flush,
    hydrate,
    persist,
    store,
    audit,
    catalog,
  };
};

// Re-exports so consumers can import everything from
// `@inside-us-aos/form-engine` without reaching into sub-modules.
export type {
  AnswerChangeListener,
  AuditEvent,
  AuditEventListener,
  AuditPayload,
  FieldStatus,
  PersistedLoadResult,
  PersistedSnapshot,
  RedactedValue,
  RepeatableGroupState,
  SectionProgress,
  ValidateAnswersResult,
  ValidationIssue,
};
